/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSaved, useUpdateSavedListing } from '@lib/user-context';

// ---------------------------------------------------------

import Button from '@components/button';
import Checkboxes from '@components/checkbox';
import Icon from '@components/icon';
import Card from '@components/card';
import Modal from '@components/modal';

// ---------------------------------------------------------

import styles from './styles.module.scss';
const {
  notification_button,
  notification_card_content,
  notification_card_property,
  notification_card,
  notification_checkboxes,
  remove_notification_button,
  remove_notification_modal
} = styles;

// ---------------------------------------------------------

const NotificationCard = (props) => {
  let {
    button,
    selectedNotifications = [],
    checkboxes,
    heading,
    property,
    listingId,
    url,
    image,
    savedListingId,
    updateNotificationListings,
    propertyNotificationModalIsOpen,
    setPropertyNotificationModalIsOpen
  } = props;

  const { address, city, priceFormatted, state, zip } = property;

  // ---------------------------------------------------------

  const [isSelected, setNotificationSelections] = useState(
    selectedNotifications.length > 0 ? selectedNotifications : []
  );
  const [currentCheckboxes, setCheckboxes] = useState(checkboxes || []);
  const [removeAllModalIsOpen, setRemoveAllModalIsOpen] = useState(false);
  const [savedListing, setSavedListing] = useState(false);
  const updateSavedListing = useUpdateSavedListing();
  const { saved } = useSaved();

  // ---------------------------------------------------------

  useEffect(() => {
    if (savedListingId) {
      const listing = saved.find((listing) => listing.savedListingId === savedListingId);
      setSavedListing(listing);

      let initiallyChecked = [];

      const params = [
        { shouldNotifyOnOpenHouseScheduled: listing.shouldNotifyOnOpenHouseScheduled },
        { shouldNotifyOnPriceChange: listing.shouldNotifyOnPriceChange },
        { shouldNotifyOnStatusChange: listing.shouldNotifyOnStatusChange }
      ];

      params.forEach((item) => {
        for (let key in item) {
          if (item[key] === true) {
            initiallyChecked.push(key);
          }
        }
      });

      setNotificationSelections(initiallyChecked);
    }
  }, []);

  const handlePropertyDetailModal = () => {
    if (propertyNotificationModalIsOpen) {
      setPropertyNotificationModalIsOpen(false);
    }
  };

  const handleRemoveAll = async () => {
    await handleSubmit(true);
    if (updateNotificationListings) {
      updateNotificationListings();
    }
    setRemoveAllModalIsOpen(false);
  };

  const handleSubmit = async (removeAll = false) => {
    let params = {
      ...(savedListing ? savedListing : { listingId, savedListingId: 0 }),
      shouldNotifyOnOpenHouseScheduled: false,
      shouldNotifyOnPriceChange: false,
      shouldNotifyOnStatusChange: false
    };

    if (!removeAll) {
      isSelected.forEach((el) => {
        // set checkbox value for open house & price / status change
        params[el] = true;
      });
    }

    updateSavedListing(params);

    setCheckboxes({
      items: [
        {
          isChecked: params.shouldNotifyOnPriceChange,
          key: 'priceChange',
          label: 'Price change',
          value: 'shouldNotifyOnPriceChange'
        },
        {
          isChecked: params.shouldNotifyOnStatusChange,
          key: 'statusChange',
          label: 'Status change',
          value: 'shouldNotifyOnStatusChange'
        },
        {
          isChecked: params.shouldNotifyOnOpenHouseScheduled,
          key: 'openHouse',
          label: 'An open house scheduled',
          value: 'shouldNotifyOnOpenHouseScheduled'
        }
      ],
      selectAllLabel: null
    });

    let newlyChecked = [];

    const newSelections = [
      { shouldNotifyOnOpenHouseScheduled: params.shouldNotifyOnOpenHouseScheduled },
      { shouldNotifyOnPriceChange: params.shouldNotifyOnPriceChange },
      { shouldNotifyOnStatusChange: params.shouldNotifyOnStatusChange }
    ];

    newSelections.forEach((item) => {
      for (let key in item) {
        if (item[key] === true) {
          newlyChecked.push(key);
        }
      }
    });

    setNotificationSelections(newlyChecked);

    handlePropertyDetailModal();
  };

  // ---------------------------------------------------------

  const areEqual = (arr1, arr2) => {
    let n = arr1.length;
    let m = arr2.length;

    if (n != m) return false;

    arr1.sort();
    arr2.sort();

    for (let i = 0; i < n; i++) if (arr1[i] != arr2[i]) return false;

    return true;
  };

  return (
    <div className={notification_card}>
      <div className={notification_card_property}>
        {property && (
          <Card
            image={{
              height: '100px',
              src: image,
              width: '100px'
            }}
            property={{
              address: address,
              city: city,
              price: priceFormatted,
              realtor: {},
              state: state,
              zip: zip
            }}
            theme="media"
            url={url}
          />
        )}

        <button
          className={remove_notification_button}
          onClick={() => setRemoveAllModalIsOpen(true)}
          title="Remove Property Notification"
        >
          <Icon type="actions" name="trash" />
        </button>
      </div>
      {removeAllModalIsOpen && (
        <Modal
          modalIsOpen={removeAllModalIsOpen}
          contentLabel="Remove Notifications Alert"
          onModalClose={() => setRemoveAllModalIsOpen(false)}
          theme="alert"
          content={
            <div className={remove_notification_modal}>
              <h3>Remove Notifications</h3>
              <p>Please confirm that you would like to remove notifications from this listing.</p>
              <Button label="Remove Notifications" onClick={handleRemoveAll} />
            </div>
          }
        />
      )}

      <div className={notification_card_content}>
        {heading && <h3>{heading}</h3>}
        {checkboxes && (
          <div className={notification_checkboxes}>
            <Checkboxes
              selections={isSelected}
              onChange={setNotificationSelections}
              {...currentCheckboxes}
            />
          </div>
        )}
        {button && (
          <div>
            <Button
              {...button}
              label={button.label}
              className={notification_button}
              onClick={() => handleSubmit()}
              type="button"
              disabled={isSelected.length < 1 || areEqual(isSelected, selectedNotifications)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

NotificationCard.propTypes = {
  /**
   * Specifies the properties that are passed to the Button component
   */
  button: PropTypes.object,

  /**
   * Specifies the properties that are passed to the Checkbox component
   */
  checkboxes: PropTypes.object,

  /**
   * Specifies a function to handle submit
   */
  handleSubmit: PropTypes.func,

  /**
   * Specifies the heading above the
   */
  heading: PropTypes.string,

  /**
   * Specifies the image src and alt text
   */
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),

  listingId: PropTypes.number,

  /**
   * Specifies the properties that are passed to the Property Card component
   */
  property: PropTypes.shape({
    address: PropTypes.string,
    city: PropTypes.string,
    priceFormatted: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string
  }),

  /**
   * Specifies if the property detail notification modal is open
   */
  propertyNotificationModalIsOpen: PropTypes.bool,

  savedListingId: PropTypes.number,

  /**
   * Specifies the currently selected notifications
   */
  selectedNotifications: PropTypes.array,

  /**
   * Function to handle the property detail notification modal on submit
   */
  setPropertyNotificationModalIsOpen: PropTypes.func,

  updateNotificationListings: PropTypes.func,
  /**
   * Specifies the url
   */
  url: PropTypes.string
};

export default NotificationCard;
